/** @format */

import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { PlatformResellerLoader } from '../platformreseller/PlatformResellerLoader';
import { IccidMvnoStatusHelper } from '../../views/lib/search/IccidMvnoStatusHelper';
import { SimCardMnoTariffLoader } from '../tariffs/SimCardMnoTariffLoader';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { PlatformIccidPoolNumberCollectionInterface } from '../../interfaces/collections/PlatformIccidPoolNumberCollectionInterface';
import i18next from 'i18next';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import { RmoListHelper } from '../rmo/RmoListHelper';
import { iccidStatus } from '../../helpers/iccidStatus';
import { PlatformRmoCollectionInterface, PlatformRmoInterface } from '../../interfaces/collections/PlatformRmoInterface';
import { DateHelperTs } from '../../helpers/DateHelperTs';

const _ = require('underscore');

interface StorageIccidToCsvPartner {
    iccid: string;
    imsi: string;
    iccidType: string | null;
    msisdn: string | null;
    // mvnoStatus: string | null;
    // mvnoStatusId: number | null;
    iccidState: string;
    // defaultTariffName: string | null;
    puk1: string | null;
    puk2: string | null;
    pin1: string | null;
    pin2: string | null;
}

interface StorageIccidToCsvSuperUser extends StorageIccidToCsvPartner {
    // rmoModelName: string | null;
    // rmoModelSetup: string | null;
    partnerName: string | null;
    defaultTariff: string | null;
    createdAt: string;
    sharedDate: string | null;
    // rmoModelMvne: string | null;
    // rmoModelMvneSetup: string | null;
    // rmoModelPartner: string | null;
    // rmoModelPartnerSetup: string | null;
}

export class StorageIccidToCsv {
    private readonly itemLoader: ItemLoaders;
    private readonly platformResellerLoader: PlatformResellerLoader;
    private readonly iccidStatusHelper: IccidMvnoStatusHelper;
    private readonly simCardMnoTariffLoader: SimCardMnoTariffLoader;

    private platformRmoCollection: PlatformRmoCollectionInterface;

    constructor(
        itemLoader: ItemLoaders,
        platformResellerLoader: PlatformResellerLoader,
        iccidStatusHelper: IccidMvnoStatusHelper,
        simCardMnoTariffLoader: SimCardMnoTariffLoader,
        platformRmoCollection: PlatformRmoCollectionInterface
    ) {
        this.itemLoader = itemLoader;
        this.platformResellerLoader = platformResellerLoader;
        this.iccidStatusHelper = iccidStatusHelper;
        this.simCardMnoTariffLoader = simCardMnoTariffLoader;
        this.platformRmoCollection = platformRmoCollection;
    }

    exportToCsv = (isSuperUser: boolean, orders: Array<Order>, filters: Array<Filter>, showMainLoader: (show: boolean) => void): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({ name: 'pagination', value: 'false' });
        showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_iccid_pool_numbers')),
            'storage_imsi_loader_items',
            extendFilter,
            orders,
            (items: PlatformIccidPoolNumberCollectionInterface): void => {
                if (isSuperUser) {
                    this.prepareSuperUserCsv(items);
                } else {
                    this.preparePartnerUserCsv(items);
                }
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    prepareSuperUserCsv = (items: PlatformIccidPoolNumberCollectionInterface): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'iccidPoolsReport_' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'PliCbd2 report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('pools:pool.iccid'),
                i18next.t('pools:pool.imsi'),
                i18next.t('pools:pool.iccid_type'),
                i18next.t('pools:pool.msisdn'),
                // i18next.t('simcard:list.status_mvno_name'),
                // i18next.t('simcard:list.status_mvno'),
                i18next.t('pools:sim.iccid_state'),
                i18next.t('prov:list.partner'),
                i18next.t('pools:sim.service_profile'),
                i18next.t('pools:pool.puk1'),
                i18next.t('pools:pool.puk2'),
                i18next.t('pools:pool.pin1'),
                i18next.t('pools:pool.pin2'),

                // i18next.t('simcard:list.status_rmo'),
                // i18next.t('simcard:list.status_date'),

                // i18next.t('pools:pool.mvno_tariff'),
                i18next.t('pools:pool.created_at'),
                i18next.t('pools:pool.share_date'),
                // i18next.t('pools:pool.mvne_status_rmo'),
                // i18next.t('pools:pool.mvne_status_date_rmo'),
                // i18next.t('pools:pool.partner_status_rmo'),
                // i18next.t('pools:pool.partner_status_date_rmo'),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    };

    getRmoDescription = (rmoId: number): string => {
        if (this.platformRmoCollection) {
            const element = _.find(this.platformRmoCollection['hydra:member'], (item: PlatformRmoInterface) => {
                return item.id === rmoId;
            });
            return element ? element.description : '';
        }
        return '';
    };

    preparePartnerUserCsv = (items: PlatformIccidPoolNumberCollectionInterface): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'iccidPoolsReport_' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'PliCbd2 report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('pools:pool.iccid'),
                i18next.t('pools:pool.imsi'),
                i18next.t('pools:pool.iccid_type'),
                i18next.t('pools:pool.msisdn'),
                // i18next.t('simcard:list.status_mvno_name'),
                // i18next.t('simcard:list.status_mvno'),
                i18next.t('pools:sim.iccid_state'),
                // i18next.t('pools:pool.mvno_tariff_name'),
                i18next.t('pools:pool.puk1'),
                i18next.t('pools:pool.puk2'),
                i18next.t('pools:pool.pin1'),
                i18next.t('pools:pool.pin2'),
            ],
        };
        const csvPartnerList: Array<StorageIccidToCsvPartner> = [];
        csvList.map((item) => {
            csvPartnerList.push({
                iccid: item.iccid,
                imsi: item.imsi,
                iccidType: item.iccidType,
                msisdn: item.msisdn,
                // mvnoStatus: item.mvnoStatus,
                // mvnoStatusId: item.mvnoStatusId,
                iccidState: i18next.t(iccidStatus(item.iccidState)),
                // defaultTariffName: item.defaultTariffName,
                puk1: item.puk1,
                puk2: item.puk2,
                pin1: item.pin1,
                pin2: item.pin2,
            });
        });
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvPartnerList);
    };

    prepareCsvData = (items: PlatformIccidPoolNumberCollectionInterface): Array<StorageIccidToCsvSuperUser> => {
        const csvList: Array<StorageIccidToCsvSuperUser> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((item) => {
                let msisdn = item.msisdn;
                if (item.lastmsisdn) {
                    msisdn = item.lastmsisdn;
                }
                let statusString: string | null = item.mvnoStatus ? this.iccidStatusHelper.getName(item.mvnoStatus) : null;
                if (statusString) {
                    statusString = i18next.t(statusString).toString();
                }
                const modelName = item.rmoModel ? RmoListHelper.getName(item.rmoModel) : null;
                csvList.push({
                    iccid: item.iccid ?? '',
                    imsi: item.imsi ?? '',
                    iccidType: item.iccidType ?? '',
                    msisdn: msisdn ?? '',
                    // mvnoStatus: statusString,
                    // mvnoStatusId: item.mvnoStatus,
                    iccidState: i18next.t(iccidStatus(item.iccidState)) ?? '',
                    partnerName: item.ownerPlatformResellerName ?? '',
                    defaultTariff: this.simCardMnoTariffLoader.getName(item.defaultTariff ? parseInt(item.defaultTariff) : -1) ?? '',
                    puk1: item.puk1 ?? '',
                    puk2: item.puk2 ?? '',
                    pin1: item.pin1 ?? '',
                    pin2: item.pin2 ?? '',

                    // rmoModelName: modelName ? i18next.t(modelName).toString() : null,
                    // rmoModelSetup: item.rmoModelSetup,

                    // defaultTariff: item.defaultTariff,
                    createdAt: item.createdAt ? DateHelperTs.format(item.createdAt, 'YYYY-MM-DD') : '',
                    sharedDate: item.sharedDate ? DateHelperTs.format(item.sharedDate, 'YYYY-MM-DD') : '',
                    // rmoModelMvne: this.getRmoDescription(item.rmoModelMvne ?? -1),
                    // rmoModelMvneSetup: item.rmoModelMvneSetup,
                    // rmoModelPartner: this.getRmoDescription(item.rmoModelPartner ?? -1),
                    // rmoModelPartnerSetup: item.rmoModelPartnerSetup,
                });
            });
        }
        return csvList;
    };
}
